import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  /** A filter's first option is an explicit "All …", never a blank. */
  options: SelectOption[];
  label?: string;
  hint?: ReactNode;
  /** The server's translated issue for this field: shown in place of the hint, and the field is marked invalid. */
  error?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  width?: CSSProperties['inlineSize'];
  disabled?: boolean;
  required?: boolean;
  /** For a bare filter without a visible label. */
  'aria-label'?: string;
  style?: CSSProperties;
}

/** Gap between the field and its menu, and the least room the menu needs before it flips above the field. */
const MENU_GAP = 6;
const MENU_MIN_ROOM = 160;
const MENU_MAX_HEIGHT = 320;

/** Where the popover API is missing (older browsers, jsdom) the menu stays a plain fixed element. */
const POPOVER = typeof HTMLElement !== 'undefined' && typeof HTMLElement.prototype.showPopover === 'function';

type MenuPosition = Pick<CSSProperties, 'top' | 'bottom' | 'left' | 'right' | 'minWidth' | 'maxWidth' | 'maxHeight'>;

/**
 * A combobox in brand chrome. The menu is drawn by the app (not the OS) so it opens below the
 * field, matches the brand, and sits in the top layer (popover) so dialogs and scroll areas never clip it.
 */
export function Select({
  value,
  onChange,
  options,
  label,
  hint,
  error,
  size = 'md',
  width,
  disabled,
  required,
  style,
  ...rest
}: SelectProps) {
  const id = useId();
  const listId = `${id}-list`;
  const hintId = `${id}-hint`;
  const note = error ?? hint;
  const controlRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [position, setPosition] = useState<MenuPosition>({});
  const typeahead = useRef({ text: '', at: 0 });

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    // The whole rounded field (padding and chevron included), so the menu lines up with what the user sees.
    const rect = (controlRef.current ?? trigger).getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - MENU_GAP * 2;
    const above = rect.top - MENU_GAP * 2;
    const flip = below < MENU_MIN_ROOM && above > below;
    // Anchor to the field's start edge so a menu wider than the field grows the way the text runs.
    const rtl = getComputedStyle(trigger).direction === 'rtl';
    setPosition({
      ...(rtl ? { right: window.innerWidth - rect.right } : { left: rect.left }),
      minWidth: rect.width,
      maxWidth: (rtl ? rect.right : window.innerWidth - rect.left) - MENU_GAP,
      maxHeight: Math.min(MENU_MAX_HEIGHT, flip ? above : below),
      ...(flip ? { bottom: window.innerHeight - rect.top + MENU_GAP } : { top: rect.bottom + MENU_GAP }),
    });
  }, []);

  const openMenu = () => {
    if (disabled) return;
    place();
    setActive(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  };

  const closeMenu = (refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  const choose = (index: number) => {
    const option = options[index];
    if (option && option.value !== value) onChange(option.value);
    closeMenu();
  };

  // The popover API lifts the menu into the top layer, above dialogs and clear of any overflow clipping.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!open || !list) return;
    if (POPOVER && !list.matches(':popover-open')) list.showPopover();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !listRef.current?.contains(target)) closeMenu(false);
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.children[active]?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active]);

  const findByTyping = (key: string) => {
    const now = Date.now();
    const state = typeahead.current;
    state.text = now - state.at > 600 ? key : state.text + key;
    state.at = now;
    const query = state.text.toLocaleLowerCase();
    const start = open ? active + (state.text.length === 1 ? 1 : 0) : selectedIndex + 1;
    for (let step = 0; step < options.length; step += 1) {
      const index = (Math.max(start, 0) + step) % options.length;
      if (options[index].label.toLocaleLowerCase().startsWith(query)) return index;
    }
    return -1;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const last = options.length - 1;
    const move = (index: number) => {
      event.preventDefault();
      if (open) setActive(Math.max(0, Math.min(last, index)));
      else openMenu();
    };
    switch (event.key) {
      case 'ArrowDown':
        return move(active + 1);
      case 'ArrowUp':
        return move(active - 1);
      case 'Home':
        return move(0);
      case 'End':
        return move(last);
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (open) choose(active);
        else openMenu();
        return;
      case 'Escape':
        if (!open) return;
        // Keep the Escape from also closing a surrounding dialog.
        event.preventDefault();
        event.stopPropagation();
        closeMenu();
        return;
      case 'Tab':
        if (open) closeMenu(false);
        return;
      default:
        if (event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
          const index = findByTyping(event.key);
          if (index < 0) return;
          if (open) setActive(index);
          else if (options[index].value !== value) onChange(options[index].value);
        }
    }
  };

  const classes = ['fs-control', 'fs-control--select'];
  if (size !== 'md') classes.push(`fs-control--${size}`);
  if (disabled) classes.push('fs-control--disabled');
  if (open) classes.push('fs-control--open');
  const control = (
    <span ref={controlRef} className={classes.join(' ')} style={{ inlineSize: width, ...style }}>
      <button
        ref={triggerRef}
        id={label ? id : undefined}
        type="button"
        role="combobox"
        data-value={value}
        className="fs-control__input fs-control__select"
        disabled={disabled}
        aria-label={rest['aria-label']}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={note ? hintId : undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span className="fs-control__value">{selected?.label ?? ''}</span>
      </button>
      {/* Drawn in CSS: the ▾ glyph is missing from IBM Plex and fell back to a dot-sized character. */}
      <span className="fs-control__chevron" aria-hidden="true" />
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          popover={POPOVER ? 'manual' : undefined}
          className="fs-menu"
          aria-label={label ?? rest['aria-label']}
          style={position}
        >
          {options.map((option, index) => {
            const isSelected = index === selectedIndex;
            const optionClasses = ['fs-menu__option'];
            if (isSelected) optionClasses.push('fs-menu__option--selected');
            if (index === active) optionClasses.push('fs-menu__option--active');
            return (
              <li
                key={option.value}
                id={`${listId}-${index}`}
                role="option"
                data-value={option.value}
                aria-selected={isSelected}
                className={optionClasses.join(' ')}
                onPointerEnter={() => setActive(index)}
                // Keep focus on the field so the menu doesn't close before the click lands.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(index)}
              >
                <span className="fs-menu__label">{option.label}</span>
                <span className="fs-menu__check" aria-hidden="true" />
              </li>
            );
          })}
        </ul>
      )}
    </span>
  );
  if (!label && !note) return control;
  return (
    <div className={error ? 'fs-field fs-field--invalid' : 'fs-field'}>
      {label && (
        <label className="fs-field__label" htmlFor={id}>
          {label}
        </label>
      )}
      {control}
      {note && (
        <span id={hintId} className="fs-field__hint">
          {note}
        </span>
      )}
    </div>
  );
}
